// Tips: card alerts (Chase among them) carry the pre-tip amount, never the final total. A tip is
// stored next to the bank charge (transactions.tip_cents); the budget counts charge + tip, or the
// split share of that total. The maths and every line of tip copy, shared by the Worker and the PWA.

/** The quick picks, in percent of the bank charge. */
export const TIP_PERCENTS = [15, 18, 20] as const;

/** A tip of `pct` percent of the charge, to the nearest cent (half a cent rounds up). */
export const tipFor = (chargeCents: number, pct: number) => Math.round((chargeCents * pct) / 100);

/**
 * "Final total": the tip that makes the charge add up to it. null when the total is below the
 * charge (refused), 0 when it equals the charge (no tip).
 */
export function tipFromTotal(chargeCents: number, totalCents: number): number | null {
  if (!Number.isInteger(totalCents) || totalCents < chargeCents) return null;
  return totalCents - chargeCents;
}

/** What a purchase adds up to: the charge plus its tip. Splits share this total. */
export const tippedTotal = (chargeCents: number, tipCents: number | null | undefined) => chargeCents + (tipCents ?? 0);

/** Category names that are usually tipped: dining, bars, cafes, hair, beauty, delivery. */
const TIPPED_CATEGORY = /\b(din(e|ing)|eat(ing)?\s*out|restaurants?|takeaway|take-?out|bars?|drinks?|pubs?|caf(e|é)s?|coffee|hair|beauty|salons?|nails?|barbers?|spa|delivery)\b/i;

/**
 * Bank text that looks like a restaurant, bar, cafe, salon or delivery: Toast (TST*) and the
 * delivery apps, and the plain words. Matched on the raw alert text, case-insensitive.
 */
const TIPPED_TEXT = /(^|[^a-z])(doordash|uber\s*\*?\s*eats|ubereats|grubhub|postmates|caviar|seamless|instacart|cafe|caf\u00e9|coffee|bar|bistro|grill|tavern|pub|brewing|brewery|kitchen|pizza|pizzeria|sushi|taqueria|diner|restaurant|salon|barber|nails|spa)([^a-z]|$)/i;
/** Toast's prefix ("TST* JOES", "TST*BLUE DOOR"): restaurants. */
const TOAST_PREFIX = /^\s*tst\s*\*/i;

/**
 * Show "Add tip?" up front: the merchant is marked "Usually tipped", its category looks tipped,
 * or the bank text does. Everywhere else the control stays a quiet link. No model call.
 */
export function tipLikely(t: { usuallyTipped?: boolean; categoryName?: string | null; bankText?: string | null }): boolean {
  if (t.usuallyTipped) return true;
  if (t.categoryName && TIPPED_CATEGORY.test(t.categoryName)) return true;
  return !!t.bankText && (TOAST_PREFIX.test(t.bankText) || TIPPED_TEXT.test(t.bankText));
}

const dollars = (cents: number) => `$${(cents / 100).toFixed(2)}`;

export const TIP_ADD = 'Add tip';
export const TIP_ADD_PROMPT = 'Add tip?';
export const TIP_USUALLY = 'Usually tipped';
export const TIP_NONE = 'No tip';
export const TIP_BELOW_CHARGE = 'The total cannot be less than the bank charge.';
/** "Total $49.68" under the tip fields, before saving. */
export const tipTotalLine = (chargeCents: number, tipCents: number) => `Total ${dollars(chargeCents + tipCents)}`;
/** The small print wherever a tipped purchase's total is the main number. */
export const tipSmallPrint = (chargeCents: number, tipCents: number) => `Bank charged ${dollars(chargeCents)}, tip ${dollars(tipCents)}`;
/** Spending only: never on money set aside, a Done entry or my share of a partner's split. */
export const TIP_NOT_HERE = 'Tips are for your own spending only.';
